import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, ExternalLink, ChevronRight, Globe, MapPin,
  Tag, Shield, Copy, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeStyle = (theme) => {
  const map = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700' };
  return map[theme] || 'bg-gray-50 text-gray-600';
};

export default function PartnerDetail() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [disclosure, setDisclosure] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/partners/detail/${slug}`).then(r => r.ok ? r.json() : Promise.reject()),
      fetch(`${API}/partners/disclosure`).then(r => r.json()),
    ]).then(([p, d]) => {
      setPartner(p);
      setDisclosure(d.text || '');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const trackClick = () => {
    if (partner) fetch(`${API}/partners/click/${partner.partner_id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_page: 'detail' }) }).catch(() => {});
  };

  const copyCoupon = () => {
    if (partner?.coupon_code) {
      navigator.clipboard.writeText(partner.coupon_code);
      setCopiedCode(true);
      toast.success('Coupon code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-gray-50 rounded-xl animate-pulse border" />)}</div>;
  if (!partner) return <div className="max-w-4xl mx-auto px-4 py-12 text-center text-gray-500">Partner not found.</div>;

  const primaryCat = partner.category_assignments?.[0];

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="partner-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2 text-xs text-gray-400">
          <Link to="/" className="hover:text-[#2563eb]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/partners" className="hover:text-[#2563eb]">Partners</Link>
          {primaryCat?.category && (
            <>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/partners/category/${primaryCat.category.slug}`} className="hover:text-[#2563eb]">{primaryCat.category.name}</Link>
            </>
          )}
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0f1f33] font-medium">{partner.name}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <Link to="/partners"><Button variant="ghost" size="sm" className="-ml-2"><ArrowLeft className="w-4 h-4 mr-1" /> All Partners</Button></Link>

        {/* Main Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-8">
            <div className="flex items-start gap-6">
              {partner.logo_url && (
                <div className="w-20 h-20 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <img src={partner.logo_url} alt={partner.name} className="w-14 h-14 object-contain" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="text-2xl font-bold text-[#0f1f33]">{partner.name}</h1>
                  {partner.badges?.map((b, i) => <Badge key={i} className={`text-xs ${badgeStyle(b.color_theme)}`}>{b.label}</Badge>)}
                </div>
                <p className="text-gray-500 leading-relaxed mb-4">{partner.short_description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  {partner.website_url && (
                    <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#2563eb]">
                      <Globe className="w-3.5 h-3.5" /> Website
                    </a>
                  )}
                  {(partner.location_city || partner.location_state) && (
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[partner.location_city, partner.location_state].filter(Boolean).join(', ')}</span>
                  )}
                  {partner.serves_national && <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Serves Nationwide</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Discount / CTA Block */}
          <div className="border-t border-gray-100 p-6 bg-gray-50/50">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {(partner.discount_text || partner.coupon_code) && (
                <div className={`flex-1 rounded-xl p-4 ${partner.is_exclusive_discount ? 'bg-rose-50 border border-rose-100' : 'bg-emerald-50 border border-emerald-100'}`}>
                  {partner.discount_text && <p className="text-sm font-semibold text-[#0f1f33] mb-1">{partner.discount_text}</p>}
                  {partner.coupon_code && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Use code:</span>
                      <code className="bg-white px-3 py-1 rounded-md border border-gray-200 text-sm font-mono font-bold text-[#0f1f33]">{partner.coupon_code}</code>
                      <Button variant="ghost" size="sm" onClick={copyCoupon} className="h-7 px-2" data-testid="copy-coupon">
                        {copiedCode ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {partner.affiliate_url && (
                <a href={partner.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={trackClick}>
                  <Button className="bg-[#c41230] hover:bg-[#a00f28] text-white px-8 h-11" data-testid="partner-cta">
                    {partner.primary_cta_text || 'Visit Partner'} <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Full Description */}
        {partner.full_description && (
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-[#0f1f33] mb-3">About {partner.name}</h2>
              <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{partner.full_description}</div>
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        {partner.category_assignments?.length > 0 && (
          <Card className="border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-[#0f1f33] mb-3">Categories</h2>
              <div className="flex flex-wrap gap-2">
                {partner.category_assignments.map((ca, i) => (
                  <Link key={i} to={`/partners/category/${ca.category?.slug}${ca.subcategory ? `/${ca.subcategory.slug}` : ''}`}>
                    <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer px-3 py-1.5">
                      {ca.category?.name}{ca.subcategory ? ` > ${ca.subcategory.name}` : ''}
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Related Partners */}
        {partner.related_partners?.length > 0 && (
          <section>
            <h2 className="text-base font-semibold text-[#0f1f33] mb-4">Related Partners</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {partner.related_partners.map((rp, i) => (
                <Link key={i} to={`/partners/${rp.slug}`}>
                  <Card className="border-gray-100 shadow-sm hover:shadow-md transition-all">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-[#0f1f33] mb-1">{rp.name}</h3>
                      <p className="text-xs text-gray-400 line-clamp-2">{rp.short_description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Disclosure */}
        {disclosure && (
          <div className="border-t border-gray-100 pt-6">
            <p className="text-xs text-gray-400 italic">{disclosure}</p>
          </div>
        )}
      </div>
    </div>
  );
}
